import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard, Activity, Users, GraduationCap, Wallet,
  MessageSquare, BrainCircuit, Rocket, ShieldCheck, Settings,
  ChevronDown, ChevronLeft, ChevronRight, LogOut,
  AlertTriangle, Zap, DoorOpen, Bell, ClipboardList, UserCircle,
  FileText, BookOpen, Calendar, BarChart3, Receipt, CreditCard,
  Building2, Layers, ArrowLeftRight, Banknote, PieChart, Scale,
  TrendingUp, Megaphone, Target, Share2, MessageCircle, Sparkles,
  Import, Shield, Bus, ShieldAlert, AlertCircle, Archive,
  Clock, Star, Radio, ScrollText
} from 'lucide-react';
import { cn } from '../../../lib/utils';
import { usePermissoes } from '../../../lib/permissoes';
import { usePermV2 } from '../../../lib/perm_v2';

const API_URL = window.location.origin || '';

// ═══════════════════════════════════════════
// NAV DATA — all routes preserved
// ═══════════════════════════════════════════
const navSections = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/v2/dashboard', glow: '#8E52AB',
    children: [
      { label: 'Painel Global', path: '/v2/dashboard', icon: LayoutDashboard },
      { label: 'Executivo', path: '/v2/financeiro/executivo', icon: TrendingUp },
      { label: 'Alertas', path: '/v2/central', icon: AlertTriangle },
    ] },
  { id: 'operacao', label: 'Operação', icon: Activity, path: '/v2/central', badge: 'pendencias', glow: '#F59E0B',
    children: [
      { label: 'Central Operacional', path: '/v2/central', icon: ClipboardList },
      { label: 'Lembretes', path: '/v2/comunicacao/lembretes', icon: Bell },
      { divider: true, label: 'DNA GO' },
      { label: 'Painel de Saída', path: '/v2/operacao/painel', icon: DoorOpen },
      { label: 'Portaria', path: '/v2/operacao/portaria', icon: ShieldAlert },
      { label: 'Autorizados', path: '/v2/operacao/autorizados', icon: Shield },
      { label: 'Retiradas', path: '/v2/operacao/retiradas', icon: Users },
      { label: 'Vans', path: '/v2/operacao/vans', icon: Bus },
      { label: 'Ocorrências', path: '/v2/operacao/ocorrencias', icon: AlertCircle },
    ] },
  { id: 'alunos', label: 'Alunos', icon: Users, path: '/v2/aluno', glow: '#8E52AB',
    children: [
      { label: 'Aluno 360', path: '/v2/aluno', icon: UserCircle },
      { label: 'Cadastros Incompletos', path: '/v2/alunos/cadastros-incompletos', icon: AlertCircle },
      { label: 'Matrículas e Rematrículas', path: '/v2/matriculas', icon: ClipboardList },
      { label: 'Painel Operacional', path: '/v2/matriculas/painel', icon: ClipboardList },
      { label: 'Central de Documentação', path: '/v2/matriculas/documentos', icon: FileText },
      { label: 'Virada de Ano', path: '/v2/matriculas/virada-ano', icon: ClipboardList },
      { label: 'DNA Chat', path: '/v2/chat', icon: MessageSquare },
      { label: 'Carteirinhas', path: '/v2/academico/carteirinhas', icon: CreditCard },
      { label: 'Documentos', path: '/v2/academico/documentos', icon: FileText },
    ]
  },
  { id: 'secretaria', label: 'Secretaria', icon: ScrollText, path: '/v2/secretaria/historico', glow: '#8E52AB',
    children: [
      { label: 'Novos DNAs', path: '/v2/secretaria/novos-dnas', icon: Sparkles },
      { label: 'Histórico Escolar', path: '/v2/secretaria/historico', icon: ScrollText },
      { label: 'Históricos Gerados', path: '/v2/secretaria/historico/emissoes', icon: FileText },
    ] },
  { id: 'academico', label: 'Acadêmico', icon: GraduationCap, path: '/v2/academico', glow: '#6366F1',
    children: [
      { label: 'Portal Equipe DNA', path: '/v2/academico/portal-professor', icon: Users },
      { label: 'Turmas', path: '/v2/academico/turmas', icon: Users },
      { label: 'Agrupamentos', path: '/v2/academico/agrupamentos', icon: Layers },
      { label: 'Professores', path: '/v2/academico/professores', icon: UserCircle },
      { label: 'Horários', path: '/v2/academico/horarios', icon: Clock },
      { label: 'Avaliações', path: '/v2/academico/avaliacoes', icon: FileText },
      { label: 'Notas', path: '/v2/academico/notas', icon: BarChart3 },
      { label: 'Pendências Acadêmicas', path: '/v2/academico/pendencias', icon: AlertCircle },
      { label: 'Boletim', path: '/v2/academico/boletim', icon: BookOpen },
      { label: 'DNA IA Estudos', path: '/v2/academico/dna-estudos', icon: Sparkles },
      { label: 'Matrizes', path: '/v2/academico/matrizes', icon: Layers },
      { label: 'Cursos', path: '/v2/academico/cursos', icon: GraduationCap },
      { label: 'Agenda', path: '/v2/academico/agenda', icon: Calendar },
      { label: 'Calendário', path: '/v2/academico/calendario', icon: Calendar },
      { label: 'Carteirinhas', path: '/v2/academico/carteirinhas', icon: CreditCard },
      { label: 'Documentos', path: '/v2/academico/documentos', icon: FileText },
      { divider: true, label: 'EDUCAÇÃO INFANTIL' },
      { label: 'DNA Infantil 360 IA', path: '/v2/infantil', icon: Star },
    ] },
  { id: 'financeiro', label: 'Financeiro', icon: Wallet, path: '/v2/financeiro', glow: '#10B981',
    children: [
      { label: 'Visão Geral', path: '/v2/financeiro', icon: PieChart },
      { divider: true, label: 'DNA PAY' },
      { label: 'Dashboard', path: '/v2/financeiro/dnapay', icon: Banknote, permV2: { module: 'dnapay', verb: 'visualizar' } },
      { label: 'Títulos', path: '/v2/financeiro/dnapay/titulos', icon: Receipt, permV2: { module: 'dnapay', verb: 'visualizar' } },
      { label: 'Saúde Financeira', path: '/v2/financeiro/dnapay/saude', icon: Activity, permV2: { module: 'dnapay', verb: 'visualizar' } },
      { label: 'Gerar Títulos / Boletos', path: '/v2/financeiro/gerar-titulos', icon: FileText },
      { label: 'Remessas CNAB', path: '/v2/financeiro/dnapay/remessas', icon: ArrowLeftRight },
      { label: 'Retornos CNAB', path: '/v2/financeiro/dnapay/retornos', icon: Import },
      { label: 'Cobranças Automáticas', path: '/v2/financeiro/cobrancas-auto', icon: Clock },
      { label: 'Importar Títulos', path: '/v2/financeiro/importar', icon: Import },
      { label: 'Configurações DNA Pay', path: '/v2/financeiro/config', icon: Layers },
      { divider: true, label: 'PLANOS · CAIXA' },
      { label: 'Central de Lançamentos', path: '/v2/financeiro/central-lancamentos', icon: FileText },
      { label: 'Planos', path: '/v2/financeiro/planos', icon: Layers },
      { label: 'Planos por Turma', path: '/v2/financeiro/planos-turma', icon: Layers },
      { label: 'Caixa · DRE · IA', path: '/v2/financeiro/dre-caixa-ia', icon: Layers },
      { label: 'Conciliação', path: '/v2/financeiro/conciliacao', icon: ArrowLeftRight },
      { label: 'Contas Bancárias', path: '/v2/financeiro/contas', icon: Building2 },
      { label: 'Despesas', path: '/v2/financeiro/despesas', icon: CreditCard },
      { label: 'Lançamentos', path: '/v2/financeiro/lancamentos', icon: FileText },
      { label: 'DRE', path: '/v2/financeiro/dre', icon: BarChart3 },
      { label: 'Inadimplentes', path: '/v2/financeiro/inadimplentes', icon: AlertTriangle },
      { label: 'Renegociação', path: '/v2/financeiro/renegociacao', icon: ArrowLeftRight },
      { label: 'Central de Recuperação', path: '/v2/financeiro/central-recuperacao', icon: ShieldAlert, permV2: { module: 'cobranca', verb: 'visualizar' } },
      { label: 'Cobrança Extrajudicial', path: '/v2/financeiro/cobranca-extrajudicial', icon: ShieldAlert, permV2: { module: 'cobranca', verb: 'visualizar' } },
      { label: 'Folha Pagamento', path: '/v2/financeiro/folha', icon: Banknote },
      { divider: true, label: 'FISCAL' },
      { label: 'Fiscal / NFS-e', path: '/v2/financeiro/fiscal', icon: Scale },
      { label: 'Tributário', path: '/v2/financeiro/tributario', icon: Scale },
      { divider: true, label: 'EXECUTIVO' },
      { label: 'Painel Executivo', path: '/v2/financeiro/executivo', icon: TrendingUp },
      { label: 'Analytics', path: '/v2/financeiro/analytics', icon: BarChart3 },
      { label: 'Relatórios', path: '/v2/financeiro/relatorios', icon: BarChart3 },
    ] },
  { id: 'comunicacao', label: 'Comunicação', icon: MessageSquare, path: '/v2/comunicacao', glow: '#06B6D4',
    children: [
      { label: 'Central', path: '/v2/comunicacao/mensagens', icon: MessageSquare },
      { label: 'Mural', path: '/v2/comunicacao/mural', icon: Megaphone },
      { label: 'Campanhas', path: '/v2/comunicacao/campanhas', icon: Target },
      { label: 'Templates', path: '/v2/comunicacao/templates', icon: FileText },
      { label: 'Alertas', path: '/v2/comunicacao/alertas', icon: AlertCircle },
      { label: 'Aniversariantes', path: '/v2/comunicacao/aniversariantes', icon: Star },
      { divider: true, label: 'DNACHAT · WHATSAPP' },
      { label: 'DNA Chat', path: '/v2/chat', icon: MessageSquare },
      { label: 'WhatsApp · Config', path: '/v2/chat/whatsapp/config', icon: Settings, permV2: { module: 'comunicacao', verb: 'configurar' } },
      { label: 'WhatsApp · Fila', path: '/v2/chat/whatsapp/queue', icon: ClipboardList, permV2: { module: 'comunicacao', verb: 'visualizar' } },
      { label: 'WhatsApp · Falhas', path: '/v2/chat/whatsapp/failures', icon: AlertTriangle, permV2: { module: 'comunicacao', verb: 'visualizar' } },
      { label: 'WhatsApp · Templates', path: '/v2/chat/whatsapp/templates', icon: FileText, permV2: { module: 'comunicacao', verb: 'visualizar' } },
      { divider: true, label: 'EVENTOS' },
      { label: 'Eventos (config)', path: '/v2/festa-junina/eventos', icon: Star, permV2: { module: 'festa_junina', verb: 'configurar' } },
      { label: 'Festa Junina 2026', path: '/v2/festa-junina', icon: Star, permV2: { module: 'festa_junina', verb: 'visualizar' } },
      { label: 'Sorteio', path: '/v2/festa-junina/sorteio', icon: Star, permV2: { module: 'festa_junina', verb: 'visualizar' } },
      { label: 'Check-in (QR)', path: '/v2/festa-junina/checkin', icon: Target, permV2: { module: 'festa_junina', verb: 'visualizar' } },
      { divider: true, label: 'DNA CONNECT' },
      { label: 'Central Admin', path: '/v2/familia/admin', icon: Settings },
      { label: 'Aulas Extras', path: '/v2/familia/aulas-extras', icon: BookOpen },
    ] },
  { id: 'inteligencia', label: 'Inteligência', icon: BrainCircuit, path: '/v2/ia', glow: '#3B82F6',
    children: [
      { label: 'Painel IA', path: '/v2/ia', icon: BrainCircuit },
      { label: 'Assistente IA', path: '/v2/ia/assistente', icon: Sparkles },
      { label: 'Fluência', path: '/v2/ia/fluencia', icon: BrainCircuit },
      { label: 'Tabuada', path: '/v2/ia/tabuada', icon: Target },
      { label: 'Quiz Milionário', path: '/v2/ia/quiz', icon: Star },
      { label: 'Quiz Live', path: '/v2/ia/quiz-live', icon: Zap },
      { label: 'Interclasse', path: '/v2/ia/interclasse', icon: Target },
      { label: 'Torneio', path: '/v2/ia/torneio', icon: Star },
    ] },
  { id: 'growth', label: 'Growth', icon: Rocket, path: '/v2/growth', glow: '#F97316',
    children: [
      { label: 'Dashboard', path: '/v2/growth', icon: Rocket },
      { label: 'CRM Leads', path: '/v2/growth/crm', icon: Users },
      { label: 'Social AI', path: '/v2/growth/social', icon: Share2 },
      { label: 'Atendimento AI', path: '/v2/growth/atendimento', icon: MessageCircle },
      { label: 'Ads AI', path: '/v2/growth/ads', icon: Target },
      { label: 'Funnel AI', path: '/v2/growth/funnel', icon: TrendingUp },
      { label: 'WhatsApp AI', path: '/v2/growth/whatsapp', icon: MessageCircle },
      { label: 'Reputation AI', path: '/v2/growth/reputation', icon: Star },
    ] },
  { id: 'admin', label: 'Administração', icon: ShieldCheck, path: '/v2/configuracoes', glow: '#8E52AB',
    children: [
      { label: 'Equipe DNA', path: '/v2/configuracoes/equipe', icon: Users },
      { label: 'Usuários', path: '/v2/configuracoes/usuarios', icon: Shield, permV2: { module: 'usuarios', verb: 'visualizar' } },
      { label: 'Permissões V2', path: '/v2/permissoes-v2', icon: ShieldCheck, permV2: { module: 'permissoes', verb: 'visualizar' } },
      { label: 'Ponto', path: '/v2/configuracoes/ponto', icon: Clock },
      { label: 'Logs', path: '/v2/configuracoes/logs', icon: FileText },
      { label: 'Importar/Exportar', path: '/v2/configuracoes/importar', icon: Import },
      { label: 'Acervo Sponte', path: '/v2/acervo-sponte', icon: Archive, adminOnly: true },
    ] },
  { id: 'relatorios', label: 'Relatórios', icon: BarChart3, path: '/v2/relatorios', glow: '#8E52AB',
    children: [
      { label: 'Geral', path: '/v2/relatorios/geral', icon: FileText },
      { label: 'Consolidado', path: '/v2/relatorios/consolidado', icon: BarChart3 },
      { label: 'Expansão', path: '/v2/relatorios/expansao', icon: TrendingUp },
      { label: 'Custos', path: '/v2/relatorios/custos', icon: Wallet },
    ] },
  { id: 'sistema', label: 'Sistema', icon: Settings, path: '/v2/configuracoes/sistema', glow: '#64748B',
    children: [
      { label: 'Configurações', path: '/v2/configuracoes/sistema', icon: Settings },
      { label: 'Auditoria', path: '/v2/configuracoes/logs', icon: Shield },
    ] },
];

// ═══════════════════════════════════════════
// SECTION → PERMISSION CATEGORIES MAP
// ═══════════════════════════════════════════
const sectionCategories = {
  dashboard: ['executivo', 'relatorios', 'financeiro'],
  operacao: ['dnago'],
  alunos: ['pedagogico'],
  academico: ['pedagogico', 'dnacore'],
  financeiro: ['financeiro', 'fiscal', 'tributario', 'executivo'],
  comunicacao: ['marketing', 'administrativo', 'dnaconnect'],
  inteligencia: ['dna_ia'],
  growth: ['growth'],
  admin: ['administrativo'],
  relatorios: ['relatorios', 'estrategico'],
  sistema: ['administrativo'],
};

// ═══════════════════════════════════════════
// DNA ENERGY LINE
// ═══════════════════════════════════════════
const DnaEnergyLine = () => (
  <div className="absolute left-[39px] top-0 bottom-0 w-px pointer-events-none z-0 overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-600/20 to-transparent" />
    <motion.div className="absolute left-0 w-px h-16"
      style={{ background: 'linear-gradient(to bottom, transparent, #A78BFA, transparent)' }}
      animate={{ y: ['-64px', '900px'] }}
      transition={{ duration: 6, repeat: Infinity, ease: 'linear' }} />
    <motion.div className="absolute left-0 w-px h-8"
      style={{ background: 'linear-gradient(to bottom, transparent, #818CF8, transparent)' }}
      animate={{ y: ['-32px', '900px'] }}
      transition={{ duration: 10, repeat: Infinity, ease: 'linear', delay: 3 }} />
    {/* Energy nodes */}
    {[120, 280, 440, 600].map((top, i) => (
      <motion.div key={i} className="absolute left-[-2px] w-[5px] h-[5px] rounded-full bg-purple-400/40"
        style={{ top }}
        animate={{ opacity: [0.2, 0.7, 0.2], scale: [0.8, 1.2, 0.8] }}
        transition={{ duration: 3, repeat: Infinity, delay: i * 0.8 }} />
    ))}
  </div>
);

// ═══════════════════════════════════════════
// MAIN SIDEBAR
// ═══════════════════════════════════════════
export default function Sidebar2({ collapsed, onToggle, pendencias = 0 }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState({});
  const [mission, setMission] = useState({ pendencias: 0, receita: 0, alunos: 0 });
  const [focusItems, setFocusItems] = useState([]);
  const { temAcessoCategoria, isAdmin, loading: permLoading } = usePermissoes();
  const { can: canV2, perms: permV2Data } = usePermV2();

  // Filter nav sections based on user permissions (legado + V2)
  const filteredSections = useMemo(() => {
    // Show all sections while permissions are loading or for admin
    if (permLoading || isAdmin()) return navSections;
    // Aplica gate V2 nos children (permV2: { module, verb })
    const gateChildren = (children) => (children || []).filter((c) => {
      if (c.divider) return true;
      if (c.adminOnly) return false;
      if (!c.permV2) return true; // sem tag V2 — usa legado
      if (permV2Data?.is_admin_bypass) return true;
      return canV2(c.permV2.module, c.permV2.verb);
    });
    return navSections
      .map((section) => ({ ...section, children: gateChildren(section.children) }))
      .filter((section) => {
        // remove seções que ficaram com 0 children visíveis após o gate V2
        if (section.children && section.children.length === 0) return false;
        const cats = sectionCategories[section.id];
        if (!cats) return true;
        return cats.some(cat => temAcessoCategoria(cat));
      });
  }, [temAcessoCategoria, isAdmin, permLoading, canV2, permV2Data]);

  // Load mission data
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const h = token ? { Authorization: `Bearer ${token}` } : {};
        const [dashRes, centralRes] = await Promise.all([
          fetch(`${API_URL}/api/v2/dashboard`, { headers: h }),
          fetch(`${API_URL}/api/v2/central`, { headers: h }),
        ]);
        if (dashRes.ok) {
          const d = await dashRes.json();
          setMission({
            pendencias: d.kpis?.pendencias_total || 0,
            receita: d.kpis?.receita_recebida || 0,
            alunos: d.kpis?.alunos_ativos || 0,
          });
        }
        if (centralRes.ok) {
          const c = await centralRes.json();
          const items = (c.pendencias || []).slice(0, 2).map(p => p.titulo);
          setFocusItems(items);
        }
      } catch {}
    })();
  }, []);

  const isActive = useCallback((path) => location.pathname === path, [location.pathname]);
  const isSectionActive = useCallback((s) => {
    if (isActive(s.path)) return true;
    return s.children?.some(c => !c.divider && isActive(c.path));
  }, [isActive]);

  useEffect(() => {
    filteredSections.forEach(s => {
      if (isSectionActive(s)) setExpanded(p => ({ ...p, [s.id]: true }));
    });
  }, [location.pathname, isSectionActive, filteredSections]);

  const toggleSection = (id) => {
    if (collapsed) {
      onToggle?.();
      setTimeout(() => setExpanded(p => ({ ...p, [id]: true })), 280);
    } else {
      setExpanded(p => ({ ...p, [id]: !p[id] }));
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const user = useMemo(() => JSON.parse(localStorage.getItem('user') || '{}'), []);
  const fmtK = (v) => v >= 1000 ? `R$ ${(v / 1000).toFixed(1)}k` : `R$ ${v}`;

  return (
    <aside data-testid="sidebar-v2"
      className={cn("fixed left-0 top-0 h-screen flex flex-col z-50 transition-all duration-300 overflow-hidden",
        collapsed ? "w-[80px]" : "w-[300px]"
      )}
      style={{ background: 'linear-gradient(180deg, #07011A 0%, #0C0225 50%, #130435 100%)' }}>

      {/* Ambient glows */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[20%] left-1/2 -translate-x-1/2 w-48 h-48 rounded-full opacity-[0.03]" style={{ background: 'radial-gradient(circle, #8E52AB, transparent 70%)' }} />
        <div className="absolute bottom-[30%] left-[20%] w-32 h-32 rounded-full opacity-[0.02]" style={{ background: 'radial-gradient(circle, #3B82F6, transparent 70%)' }} />
      </div>

      {/* DNA Energy Line */}
      {!collapsed && <DnaEnergyLine />}

      {/* Right border glow */}
      <div className="absolute right-0 top-0 bottom-0 w-px bg-gradient-to-b from-purple-500/10 via-purple-500/5 to-transparent" />

      {/* ═══ LOGO ═══ */}
      <div className={cn("h-14 flex items-center px-4 flex-shrink-0 relative z-10", collapsed ? "justify-center" : "justify-between")}>
        {!collapsed ? (
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center relative overflow-hidden">
              <div className="absolute inset-0 rounded-lg" style={{ background: 'linear-gradient(135deg, #8E52AB, #64327D)' }} />
              <motion.div className="absolute inset-0 rounded-lg opacity-40"
                animate={{ background: ['linear-gradient(0deg, #8B5CF680, transparent)', 'linear-gradient(360deg, #8B5CF680, transparent)'] }}
                transition={{ duration: 4, repeat: Infinity }} />
              <span className="relative text-white font-bold text-sm">D</span>
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-[15px] font-bold text-white/90 tracking-tight">DNACore</span>
              <span className="text-[8px] font-bold px-1.5 py-px rounded-full border" style={{ background: '#8B5CF615', borderColor: '#8B5CF625', color: '#A78BFA' }}>2.0</span>
            </div>
          </div>
        ) : (
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #8E52AB, #64327D)' }}>
            <span className="text-white font-bold text-sm">D</span>
          </div>
        )}
        {!collapsed && (
          <button data-testid="sidebar-collapse-btn" onClick={onToggle} className="p-1 rounded-md hover:bg-white/5 transition-all">
            <ChevronLeft className="h-4 w-4 text-white/25" />
          </button>
        )}
      </div>

      {/* ═══ MISSION STATUS ═══ */}
      {!collapsed && (
        <div className="mx-3 mb-1 relative z-10">
          <div className="p-3 rounded-xl border border-white/[0.04] relative overflow-hidden" style={{ background: 'rgba(139,92,246,0.04)' }}>
            <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #8E52AB, transparent)' }} />
            <div className="flex items-center gap-1.5 mb-2">
              <motion.div className="w-1.5 h-1.5 rounded-full bg-emerald-400"
                animate={{ opacity: [1, 0.3, 1] }} transition={{ duration: 2, repeat: Infinity }} />
              <span className="text-[8px] font-bold uppercase tracking-[0.25em] text-white/30">DNA Control Status</span>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
              {mission.pendencias > 0 && (
                <button data-testid="mission-pendencias" onClick={() => navigate('/v2/central')} className="flex items-center gap-1.5 group">
                  <motion.div className="w-1 h-1 rounded-full bg-amber-400 flex-shrink-0"
                    animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 2, repeat: Infinity }} />
                  <span className="text-[10px] text-amber-300/70 group-hover:text-amber-200 transition-colors">{mission.pendencias} pendências</span>
                </button>
              )}
              <div className="flex items-center gap-1.5">
                <motion.div className="w-1 h-1 rounded-full bg-emerald-400 flex-shrink-0"
                  animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 2, repeat: Infinity, delay: 0.5 }} />
                <span className="text-[10px] text-emerald-300/60">{fmtK(mission.receita)}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <motion.div className="w-1 h-1 rounded-full bg-purple-400 flex-shrink-0"
                  animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 2, repeat: Infinity, delay: 1 }} />
                <span className="text-[10px] text-purple-300/60">{mission.alunos} alunos</span>
              </div>
              <div className="flex items-center gap-1.5">
                <motion.div className="w-1 h-1 rounded-full bg-cyan-400 flex-shrink-0"
                  animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 2, repeat: Infinity, delay: 1.5 }} />
                <span className="text-[10px] text-cyan-300/60">Ativo</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ MISSION FOCUS ═══ */}
      {!collapsed && focusItems.length > 0 && (
        <div className="mx-3 mb-2 relative z-10">
          <div className="px-3 py-2 rounded-lg border border-white/[0.03]" style={{ background: 'rgba(245,158,11,0.03)' }}>
            <div className="flex items-center gap-1.5 mb-1">
              <Zap className="h-2.5 w-2.5 text-amber-400/60" />
              <span className="text-[8px] font-bold uppercase tracking-[0.2em] text-white/20">Foco da Missão</span>
            </div>
            {focusItems.map((item, i) => (
              <p key={i} className="text-[10px] text-white/30 truncate leading-relaxed">{item}</p>
            ))}
          </div>
        </div>
      )}

      {/* ═══ NAVIGATION ═══ */}
      <nav className="flex-1 overflow-y-auto px-2 py-1 relative z-10" style={{ scrollbarWidth: 'none' }}>
        {filteredSections.map((section) => {
          const Icon = section.icon;
          const active = isSectionActive(section);
          const isOpen = expanded[section.id];
          const hasBadge = section.badge === 'pendencias' && pendencias > 0;
          const glowColor = section.glow || '#8E52AB';

          return (
            <div key={section.id} className="mb-px">
              <button data-testid={`sidebar-nav-${section.id}`}
                onClick={() => toggleSection(section.id)}
                className={cn("w-full flex items-center gap-3 px-3 py-2 rounded-xl transition-all duration-200 relative group",
                  active ? "text-white" : "text-white/35 hover:text-white/60 hover:bg-white/[0.02]"
                )}>
                {/* Active bar */}
                {active && (
                  <motion.div layoutId="navActive"
                    className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r-full"
                    style={{ backgroundColor: glowColor, boxShadow: `0 0 12px ${glowColor}80, 0 0 4px ${glowColor}40` }}
                    transition={{ type: 'spring', stiffness: 400, damping: 35 }} />
                )}
                {/* Active bg */}
                {active && <div className="absolute inset-0 rounded-xl" style={{ background: `linear-gradient(90deg, ${glowColor}12, transparent)` }} />}

                <div className="relative z-10 flex items-center gap-3 w-full">
                  <Icon className={cn("h-[17px] w-[17px] flex-shrink-0 transition-all duration-200",
                    active && "drop-shadow-[0_0_6px_var(--glow)]"
                  )} style={active ? { color: glowColor, '--glow': `${glowColor}80` } : {}} />
                  {!collapsed && (
                    <>
                      <span className="flex-1 text-left text-sm font-medium tracking-tight">{section.label}</span>
                      {hasBadge && (
                        <span className="px-1.5 py-px text-[9px] font-bold rounded-full min-w-[20px] text-center"
                          style={{ background: `${glowColor}20`, color: glowColor, border: `1px solid ${glowColor}25` }}>
                          {pendencias > 99 ? '99+' : pendencias}
                        </span>
                      )}
                      <ChevronDown className={cn("h-3 w-3 text-white/15 transition-transform duration-200", isOpen && "rotate-180")} />
                    </>
                  )}
                </div>

                {collapsed && hasBadge && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 text-[8px] font-bold rounded-full flex items-center justify-center text-white"
                    style={{ backgroundColor: glowColor, boxShadow: `0 0 8px ${glowColor}60` }}>
                    {pendencias > 9 ? '9+' : pendencias}
                  </span>
                )}
              </button>

              {/* Submenu */}
              {!collapsed && (
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
                      className="overflow-hidden">
                      <div className="ml-[17px] pl-4 border-l border-white/[0.04] mt-px mb-1 space-y-px">
                        {section.children?.map((child, idx) => {
                          if (child.divider) {
                            return (
                              <div key={`d-${idx}`} className="pt-2 pb-1 px-2">
                                <span className="text-[7px] font-bold uppercase tracking-[0.2em] text-white/15">{child.label}</span>
                              </div>
                            );
                          }
                          const CI = child.icon;
                          return (
                            <NavLink key={child.path + idx} to={child.path}
                              data-testid={`sidebar-sub-${child.path.replace(/\//g, '-')}`}
                              className={({ isActive: a }) => cn(
                                "flex items-center gap-2.5 px-2.5 py-[6px] rounded-lg text-[12px] transition-all duration-150",
                                a ? "text-white/80 bg-white/[0.05]" : "text-white/25 hover:text-white/50 hover:bg-white/[0.02]"
                              )}>
                              <CI className="h-[13px] w-[13px] flex-shrink-0" />
                              <span className="truncate">{child.label}</span>
                            </NavLink>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              )}
            </div>
          );
        })}
      </nav>

      {/* ═══ USER ═══ */}
      <div className="border-t border-white/[0.03] p-3 flex-shrink-0 relative z-10">
        {!collapsed ? (
          <div className="flex items-center gap-2.5 px-1.5 py-1.5 rounded-xl hover:bg-white/[0.02] transition-all cursor-pointer group">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-[11px] font-bold text-white relative overflow-hidden"
              style={{ background: 'linear-gradient(135deg, #10B981, #059669)' }}>
              {(user?.name || user?.nome || 'A').charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[12px] font-medium text-white/70 truncate">{user?.name || user?.nome || 'Administrador'}</p>
              <p className="text-[10px] text-white/20 truncate">{user?.email || ''}</p>
            </div>
            <button data-testid="sidebar-logout-btn" onClick={handleLogout}
              className="p-1 rounded-md hover:bg-white/5 transition-all opacity-0 group-hover:opacity-100">
              <LogOut className="h-3.5 w-3.5 text-white/25" />
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-[11px] font-bold text-white"
              style={{ background: 'linear-gradient(135deg, #10B981, #059669)' }}>
              {(user?.name || user?.nome || 'A').charAt(0).toUpperCase()}
            </div>
            <button data-testid="sidebar-logout-btn" onClick={handleLogout}
              className="p-1 rounded-md hover:bg-white/5 transition-all">
              <LogOut className="h-3.5 w-3.5 text-white/25" />
            </button>
          </div>
        )}
      </div>

      {/* Expand button */}
      {collapsed && (
        <button data-testid="sidebar-expand-btn" onClick={onToggle}
          className="absolute -right-3 top-16 w-6 h-6 rounded-full flex items-center justify-center transition-all hover:scale-110"
          style={{ backgroundColor: '#8E52AB', boxShadow: '0 0 16px rgba(139,92,246,0.5)' }}>
          <ChevronRight className="h-3.5 w-3.5 text-white" />
        </button>
      )}
    </aside>
  );
}
